import { create } from "zustand";
import type { WidgetBranding, WidgetMessage, WidgetTheme } from "@/types";
import {
  DEFAULT_WIDGET_LOCALE,
  detectWidgetLocale,
  normalizeWidgetLocale,
  type WidgetLocale,
} from "@/lib/i18n";

export type ResolvedTheme = "light" | "dark";

interface SessionState {
  // Bootstrap
  siteKey: string | null;
  apiUrl: string | null;
  visitorId: string | null;
  conversationId: string | null;
  sessionToken: string | null;
  branding: WidgetBranding | null;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  // UI
  open: boolean;
  socketConnected: boolean;
  agentTyping: boolean;
  replyingTo: WidgetMessage | null;
  /** Files dropped on the chat window — composer consumes & clears. */
  droppedFile: File | null;
  /**
   * Tema detectado na página host (passado via postMessage pelo loader.js).
   * `null` antes do handshake; nesse caso `resolveTheme` recorre ao
   * `prefers-color-scheme` do próprio iframe como fallback.
   */
  hostTheme: ResolvedTheme | null;

  /**
   * Locale used to render all visible widget strings. Starts from the host
   * settings (`HostBridge.setSettings({ locale })`) or `navigator.language`
   * and can be updated at any time via `setLocale`.
   */
  locale: WidgetLocale;

  // Messages
  messages: WidgetMessage[];

  // Actions
  setBootstrap: (data: {
    siteKey: string;
    apiUrl: string;
    visitorId: string;
    conversationId: string | null;
    sessionToken: string;
    branding: WidgetBranding;
    history: WidgetMessage[];
  }) => void;
  /**
   * Apply the upgraded session returned by the first message — pairs a fresh
   * sessionToken (now bound to a Conversation) with the conversationId the
   * server just created. Triggers `useWebchatSocket` to reconnect via its
   * `sessionToken` dependency, joining the new conversation room.
   */
  upgradeSession: (data: {
    sessionToken: string;
    conversationId: string;
  }) => void;
  setError: (msg: string) => void;
  setOpen: (open: boolean) => void;
  setSocketConnected: (connected: boolean) => void;
  setAgentTyping: (typing: boolean) => void;
  setReplyingTo: (msg: WidgetMessage | null) => void;
  setDroppedFile: (file: File | null) => void;
  setHostTheme: (theme: ResolvedTheme | null) => void;
  setLocale: (locale: string | null | undefined) => void;
  addMessage: (msg: WidgetMessage) => void;
  replaceMessage: (tempId: string, final: WidgetMessage) => void;
  updateMessageStatus: (
    messageId: string,
    status: WidgetMessage["status"],
  ) => void;
  markMessageFailed: (tempId: string) => void;
}

export const useSession = create<SessionState>((set) => ({
  siteKey: null,
  apiUrl: null,
  visitorId: null,
  conversationId: null,
  sessionToken: null,
  branding: null,
  status: "idle",
  error: null,
  open: false,
  socketConnected: false,
  agentTyping: false,
  replyingTo: null,
  droppedFile: null,
  hostTheme: null,
  locale:
    typeof window === "undefined"
      ? DEFAULT_WIDGET_LOCALE
      : detectWidgetLocale(),
  messages: [],

  setBootstrap: (data) =>
    set({
      siteKey: data.siteKey,
      apiUrl: data.apiUrl,
      visitorId: data.visitorId,
      conversationId: data.conversationId,
      sessionToken: data.sessionToken,
      branding: data.branding,
      messages: data.history,
      status: "ready",
      error: null,
    }),

  upgradeSession: (data) =>
    set({
      sessionToken: data.sessionToken,
      conversationId: data.conversationId,
    }),

  setError: (msg) => set({ status: "error", error: msg }),

  setOpen: (open) => set({ open }),

  setSocketConnected: (socketConnected) => set({ socketConnected }),

  setAgentTyping: (agentTyping) => set({ agentTyping }),

  setReplyingTo: (replyingTo) => set({ replyingTo }),

  setDroppedFile: (droppedFile) => set({ droppedFile }),

  setHostTheme: (hostTheme) => set({ hostTheme }),

  setLocale: (locale) => set({ locale: normalizeWidgetLocale(locale) }),

  addMessage: (msg) =>
    set((state) => {
      if (state.messages.some((m) => m.id === msg.id)) return state;
      return { messages: [...state.messages, msg] };
    }),

  replaceMessage: (tempId, final) =>
    set((state) => ({
      messages: state.messages.map((m) => (m.id === tempId ? final : m)),
    })),

  updateMessageStatus: (messageId, status) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === messageId ? { ...m, status } : m,
      ),
    })),

  markMessageFailed: (tempId) =>
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === tempId ? { ...m, status: "failed", pending: false } : m,
      ),
    })),
}));

/**
 * Applies branding colors AND resolves the widget theme (light/dark).
 *
 *   - `branding.theme === 'light' | 'dark'` força o valor escolhido.
 *   - `branding.theme === 'auto'` (ou undefined) usa `hostTheme` passado pelo
 *     loader.js via postMessage; se indisponível, recorre ao
 *     `prefers-color-scheme` do próprio iframe.
 *
 * A cor primária vem do branding independente do tema.
 */
export function applyWidgetTheme(
  branding: WidgetBranding,
  hostTheme: ResolvedTheme | null,
): void {
  const root = document.documentElement;
  root.style.setProperty("--cz-primary", branding.primaryColor);
  root.style.setProperty("--cz-primary-dark", shade(branding.primaryColor, -10));
  root.style.setProperty(
    "--cz-on-primary",
    bestContrastForeground(branding.primaryColor),
  );
  const resolved = resolveTheme(branding.theme, hostTheme);
  root.setAttribute("data-cz-theme", resolved);
  // Deliberately NOT setting `root.style.colorScheme` — ver comentário em
  // tailwind.css: pintaria o canvas do iframe e vazaria sobre a página host.
}

export function resolveTheme(
  preferred: WidgetTheme | undefined,
  hostTheme: ResolvedTheme | null,
): ResolvedTheme {
  if (preferred === "light" || preferred === "dark") return preferred;
  if (hostTheme) return hostTheme;
  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
  ) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
}

function shade(hex: string, percent: number): string {
  // Lightweight HSL shift; fails gracefully to the original color.
  try {
    const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (!m) return hex;
    const [r, g, b] = [
      parseInt(m[1], 16),
      parseInt(m[2], 16),
      parseInt(m[3], 16),
    ];
    const shift = (c: number) =>
      Math.max(0, Math.min(255, c + (c * percent) / 100)) | 0;
    const toHex = (c: number) => c.toString(16).padStart(2, "0");
    return `#${toHex(shift(r))}${toHex(shift(g))}${toHex(shift(b))}`;
  } catch {
    return hex;
  }
}

function bestContrastForeground(hex: string): string {
  try {
    const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (!m) return "#0a0b0e";
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    // Relative luminance
    const l = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return l > 0.55 ? "#0a0b0e" : "#ffffff";
  } catch {
    return "#0a0b0e";
  }
}
